import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { subscribePooled } from '../../utils/realtimePoolManager';
import { classifyPaystackError, getErrorInfo, withPaystackRetry, getFallbackChannels, logPaymentFailure, queuePayoutForRetry, PAYSTACK_ERROR_CODES,  } from '../../services/paystackErrorRecoveryService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatAmount = (v, currency = 'XOF') => {
  if (!v && v !== 0) return '—';
  const opts = currency === 'XOF'
    ? { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }
    : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat('fr-FR', opts)?.format(v);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })?.format(new Date(d));
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })?.format(new Date(d));
};

// ─── Status Config ────────────────────────────────────────────────────────────

const PAYOUT_STATUS = {
  pending:        { label: 'En attente',       color: 'bg-amber-100 text-amber-700 border-amber-200',   icon: 'Clock' },
  processing:     { label: 'En cours',          color: 'bg-blue-100 text-blue-700 border-blue-200',      icon: 'RefreshCw' },
  approved:       { label: 'Approuvé',          color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: 'CheckCircle' },
  released:       { label: 'Libéré',            color: 'bg-green-100 text-green-700 border-green-200',   icon: 'CheckCircle2' },
  failed:         { label: 'Échoué',            color: 'bg-red-100 text-red-700 border-red-200',         icon: 'XCircle' },
  retry_queued:   { label: 'File de réessai',   color: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'RotateCcw' },
  on_hold:        { label: 'En suspens',        color: 'bg-gray-100 text-gray-600 border-gray-200',      icon: 'PauseCircle' },
};

const CHANNEL_CONFIG = {
  bank_transfer:  { label: 'Virement Bancaire', icon: 'Building2',  color: 'text-blue-600',   bg: 'bg-blue-50' },
  mobile_money:   { label: 'Mobile Money',      icon: 'Smartphone', color: 'text-green-600',  bg: 'bg-green-50' },
  paystack:       { label: 'Paystack',          icon: 'CreditCard', color: 'text-purple-600', bg: 'bg-purple-50' },
  orange_money:   { label: 'Orange Money',      icon: 'Smartphone', color: 'text-orange-600', bg: 'bg-orange-50' },
  mtn_momo:       { label: 'MTN MoMo',          icon: 'Smartphone', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  wave:           { label: 'Wave',              icon: 'Zap',        color: 'text-cyan-600',   bg: 'bg-cyan-50' },
  manual:         { label: 'Manuel',            icon: 'FileText',   color: 'text-gray-600',   bg: 'bg-gray-50' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LiveBadge = ({ active }) => (
  <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
    active
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :'bg-gray-100 text-gray-500 border-gray-200'
  }`}>
    <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
    {active ? 'Sync live' : 'Hors ligne'}
  </span>
);

const StatusBadge = ({ status }) => {
  const cfg = PAYOUT_STATUS?.[status] || PAYOUT_STATUS?.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      {cfg?.label}
    </span>
  );
};

const ChannelBadge = ({ channel }) => {
  const cfg = CHANNEL_CONFIG?.[channel] || CHANNEL_CONFIG?.bank_transfer;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${cfg?.bg} ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      {cfg?.label}
    </span>
  );
};

// ─── Routing Verification Card ────────────────────────────────────────────────

const RoutingVerificationCard = ({ payout, onApprove, onReject, onFallback }) => {
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showFallbackMenu, setShowFallbackMenu] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    await onApprove?.(payout);
    setApproving(false);
  };

  const handleReject = async () => {
    if (!rejectNote?.trim()) return;
    setRejecting(true);
    await onReject?.(payout, rejectNote);
    setRejecting(false);
    setShowRejectForm(false);
  };

  const bankInfo = payout?.bank_account_info || {};
  const channel = payout?.payout_channel || 'bank_transfer';
  const channelCfg = CHANNEL_CONFIG?.[channel] || CHANNEL_CONFIG?.bank_transfer;
  const statusCfg = PAYOUT_STATUS?.[payout?.routing_status || payout?.status] || PAYOUT_STATUS?.pending;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className={`w-11 h-11 rounded-xl ${channelCfg?.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={channelCfg?.icon} size={20} className={channelCfg?.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {payout?.recipient_name || payout?.user_profiles?.full_name || 'Agent inconnu'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {payout?.recipient_role === 'agent' ? 'Agent' : payout?.recipient_role === 'notary' ? 'Notaire' : 'Vendeur'}
                {payout?.transaction_ref ? ` · Réf: ${payout?.transaction_ref?.slice(0, 12)}…` : ''}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <p className="text-base font-bold text-gray-900">
                {formatAmount(payout?.net_payout_amount || payout?.amount, payout?.payout_currency || 'XOF')}
              </p>
              <StatusBadge status={payout?.routing_status || payout?.status} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <ChannelBadge channel={channel} />
            <span className="text-xs text-gray-400">{formatDate(payout?.scheduled_date || payout?.created_at)}</span>
          </div>
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-gray-400 flex-shrink-0 mt-1" />
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-4">
          {/* Bank routing details */}
          <div className="bg-gray-50 rounded-xl p-3 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
              Routage Bancaire
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div>
                <p className="text-gray-400">Banque</p>
                <p className="font-semibold text-gray-800">{bankInfo?.bank_name || payout?.bank_name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-400">N° Compte</p>
                <p className="font-semibold text-gray-800 font-mono">
                  {bankInfo?.account_number
                    ? `****${bankInfo?.account_number?.slice(-4)}`
                    : payout?.bank_account_number
                      ? `****${payout?.bank_account_number?.slice(-4)}`
                      : '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-400">Code Routage</p>
                <p className="font-semibold text-gray-800 font-mono">
                  {bankInfo?.routing_code || payout?.bank_routing_code || '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-400">IBAN / SWIFT</p>
                <p className="font-semibold text-gray-800 font-mono text-[10px]">
                  {bankInfo?.iban || payout?.iban || bankInfo?.swift || '—'}
                </p>
              </div>
              {(bankInfo?.mobile_number || payout?.mobile_number) && (
                <div className="col-span-2">
                  <p className="text-gray-400">Numéro Mobile Money</p>
                  <p className="font-semibold text-gray-800">{bankInfo?.mobile_number || payout?.mobile_number}</p>
                </div>
              )}
            </div>
          </div>

          {/* Settlement details */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-amber-600 font-medium">Montant Brut</p>
              <p className="text-base font-bold text-amber-800 mt-0.5">
                {formatAmount(payout?.gross_amount || payout?.amount, payout?.payout_currency || 'XOF')}
              </p>
            </div>
            <div className="bg-red-50 rounded-xl p-3">
              <p className="text-red-600 font-medium">Retenue Fiscale</p>
              <p className="text-base font-bold text-red-800 mt-0.5">
                {formatAmount(payout?.tax_withheld || 0, payout?.payout_currency || 'XOF')}
              </p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 col-span-2">
              <p className="text-emerald-600 font-medium">Montant Net à Verser</p>
              <p className="text-xl font-bold text-emerald-800 mt-0.5">
                {formatAmount(payout?.net_payout_amount || payout?.amount, payout?.payout_currency || 'XOF')}
              </p>
            </div>
          </div>

          {/* Batch info */}
          {payout?.batch_id && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
              <Icon name="Layers" size={13} className="text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <span className="font-semibold">Lot de traitement :</span> {payout?.batch_id?.slice(0, 16)}…
              </p>
            </div>
          )}

          {/* Error info if failed */}
          {(payout?.routing_status === 'failed' || payout?.status === 'failed') && payout?.last_error_code && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-xl">
              <Icon name="AlertTriangle" size={13} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-700">Dernière erreur</p>
                <p className="text-xs text-red-600 mt-0.5">{payout?.last_error_code}</p>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {(payout?.routing_status === 'pending' || payout?.status === 'pending') && (
            <div className="space-y-2">
              {!showRejectForm ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                  >
                    {approving
                      ? <><Icon name="Loader" size={14} className="animate-spin" />Approbation…</>
                      : <><Icon name="CheckCircle" size={14} />Approuver</>
                    }
                  </button>
                  <button
                    onClick={() => setShowRejectForm(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors"
                  >
                    <Icon name="XCircle" size={14} />
                    Rejeter
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e?.target?.value)}
                    placeholder="Motif du rejet (obligatoire)…"
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleReject}
                      disabled={rejecting || !rejectNote?.trim()}
                      className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition-colors"
                    >
                      {rejecting ? 'Rejet…' : 'Confirmer le rejet'}
                    </button>
                    <button
                      onClick={() => { setShowRejectForm(false); setRejectNote(''); }}
                      className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* Fallback channel selector */}
              <button
                onClick={() => setShowFallbackMenu(p => !p)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-50 text-gray-600 border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-100 transition-colors"
              >
                <Icon name="ArrowRightLeft" size={13} />
                Changer de canal de paiement
                <Icon name={showFallbackMenu ? 'ChevronUp' : 'ChevronDown'} size={12} />
              </button>

              {showFallbackMenu && (
                <div className="grid grid-cols-2 gap-2">
                  {getFallbackChannels(channel)?.map((fb) => {
                    const fbCfg = CHANNEL_CONFIG?.[fb?.id] || {};
                    return (
                      <button
                        key={fb?.id}
                        onClick={() => { onFallback?.(payout, fb?.id); setShowFallbackMenu(false); }}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-colors ${fbCfg?.bg} ${fbCfg?.color} border-current/20 hover:opacity-80`}
                      >
                        <Icon name={fbCfg?.icon || 'CreditCard'} size={13} />
                        {fb?.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Already approved */}
          {(payout?.routing_status === 'approved' || payout?.status === 'approved') && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Icon name="CheckCircle" size={14} className="text-emerald-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-emerald-700">
                Approuvé — En attente de traitement en lot
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Batch Summary Card ───────────────────────────────────────────────────────

const BatchSummaryCard = ({ payouts }) => {
  const pending = payouts?.filter(p => (p?.routing_status || p?.status) === 'pending')?.length || 0;
  const approved = payouts?.filter(p => (p?.routing_status || p?.status) === 'approved')?.length || 0;
  const failed = payouts?.filter(p => (p?.routing_status || p?.status) === 'failed')?.length || 0;
  const totalNet = payouts?.reduce((sum, p) => sum + (p?.net_payout_amount || p?.amount || 0), 0);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Lot de Règlement</p>
          <p className="text-2xl font-bold mt-0.5">{formatAmount(totalNet)}</p>
        </div>
        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
          <Icon name="Layers" size={22} className="text-white" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/10 rounded-xl p-2.5 text-center">
          <p className="text-xl font-bold text-amber-300">{pending}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">En attente</p>
        </div>
        <div className="bg-white/10 rounded-xl p-2.5 text-center">
          <p className="text-xl font-bold text-emerald-300">{approved}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Approuvés</p>
        </div>
        <div className="bg-white/10 rounded-xl p-2.5 text-center">
          <p className="text-xl font-bold text-red-300">{failed}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Échoués</p>
        </div>
      </div>
    </div>
  );
};

// ─── Error Recovery Banner ────────────────────────────────────────────────────

const ErrorRecoveryBanner = ({ errorCode, onRetry, onFallback, onDismiss, retryCountdown }) => {
  const info = getErrorInfo(errorCode);
  if (!info) return null;

  const severityColors = {
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error:   'bg-red-50 border-red-200 text-red-800',
    info:    'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`rounded-2xl border p-4 ${severityColors?.[info?.severity] || severityColors?.error}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center flex-shrink-0">
          <Icon name={info?.severity === 'warning' ? 'AlertTriangle' : 'XCircle'} size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold">{info?.title}</p>
          <p className="text-xs mt-1 opacity-80">{info?.message}</p>
          <p className="text-xs mt-1 font-medium opacity-90">{info?.suggestion}</p>

          {retryCountdown > 0 && (
            <p className="text-xs mt-2 font-semibold">
              Nouvelle tentative dans {Math.ceil(retryCountdown / 1000)}s…
            </p>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            {info?.canRetry && retryCountdown === 0 && (
              <button
                onClick={onRetry}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 rounded-lg text-xs font-bold hover:bg-white transition-colors"
              >
                <Icon name="RotateCcw" size={12} />
                Réessayer
              </button>
            )}
            {info?.canFallback && (
              <button
                onClick={onFallback}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 rounded-lg text-xs font-bold hover:bg-white transition-colors"
              >
                <Icon name="ArrowRightLeft" size={12} />
                Autre méthode
              </button>
            )}
            <button
              onClick={onDismiss}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/40 rounded-lg text-xs font-semibold hover:bg-white/60 transition-colors"
            >
              <Icon name="X" size={12} />
              Ignorer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Batch Approval Confirmation Modal ────────────────────────────────────────

const BatchApprovalModal = ({ approvedPayouts, onConfirm, onCancel, loading }) => {
  const total = approvedPayouts?.reduce((s, p) => s + (p?.net_payout_amount || p?.amount || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center p-0">
      <div className="bg-white rounded-t-3xl w-full max-w-lg shadow-2xl">
        <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4" />
        <div className="px-5 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Confirmer le traitement en lot</h3>
              <p className="text-sm text-gray-500">{approvedPayouts?.length} paiement{approvedPayouts?.length !== 1 ? 's' : ''} approuvé{approvedPayouts?.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-4">
            <p className="text-xs text-emerald-600 font-medium uppercase tracking-wider">Montant total à verser</p>
            <p className="text-3xl font-bold text-emerald-800 mt-1">{formatAmount(total)}</p>
          </div>

          <div className="space-y-2 mb-5 max-h-40 overflow-y-auto">
            {approvedPayouts?.map((p) => (
              <div key={p?.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {p?.recipient_name || p?.user_profiles?.full_name || 'Agent'}
                  </p>
                  <ChannelBadge channel={p?.payout_channel || 'bank_transfer'} />
                </div>
                <p className="text-sm font-bold text-gray-900">
                  {formatAmount(p?.net_payout_amount || p?.amount, p?.payout_currency || 'XOF')}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-2xl text-sm font-bold hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-600 text-white rounded-2xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-60 transition-colors"
            >
              {loading
                ? <><Icon name="Loader" size={15} className="animate-spin" />Traitement…</>
                : <><Icon name="Zap" size={15} />Lancer le lot</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

const AgentPayoutVerification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [errorState, setErrorState] = useState(null); // { errorCode, retryCountdown }
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [toast, setToast] = useState(null);
  const countdownRef = useRef(null);
  const abortRef = useRef(null);

  // ─── Toast helper ─────────────────────────────────────────────────────────

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ─── Fetch payouts ────────────────────────────────────────────────────────

  const fetchPayouts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        ?.from('commission_payouts')
        ?.select('*')
        ?.in('status', ['pending', 'approved', 'failed', 'retry_queued', 'on_hold'])
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;
      setPayouts(data || []);
    } catch (err) {
      console.error('[AgentPayoutVerification] fetchPayouts error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  // ─── Realtime subscription ────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = subscribePooled({
      channelName: 'agent:payout-verification',
      listeners: [
        { event: '*', schema: 'public', table: 'commission_payouts' },
      ],
      debounceMs: 500,
      onStatusChange: (status) => setLiveConnected(status === 'SUBSCRIBED'),
      callback: () => {
        fetchPayouts();
      },
    });
    return unsubscribe;
  }, [fetchPayouts]);

  // ─── Approve single payout ────────────────────────────────────────────────

  const handleApprove = useCallback(async (payout) => {
    try {
      const { error } = await supabase
        ?.from('commission_payouts')
        ?.update({
          routing_status: 'approved',
          status: 'approved',
          approved_by: user?.id,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', payout?.id);

      if (error) throw error;

      setPayouts(prev => prev?.map(p =>
        p?.id === payout?.id ? { ...p, routing_status: 'approved', status: 'approved' } : p
      ));
      showToast(`Paiement approuvé — ${payout?.recipient_name || 'Agent'}`);
    } catch (err) {
      const errorCode = classifyPaystackError(err);
      setErrorState({ errorCode });
      showToast('Erreur lors de l\'approbation', 'error');
    }
  }, [user?.id, showToast]);

  // ─── Reject single payout ─────────────────────────────────────────────────

  const handleReject = useCallback(async (payout, note) => {
    try {
      const { error } = await supabase
        ?.from('commission_payouts')
        ?.update({
          routing_status: 'on_hold',
          status: 'on_hold',
          rejection_note: note,
          rejected_by: user?.id,
          rejected_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', payout?.id);

      if (error) throw error;

      setPayouts(prev => prev?.map(p =>
        p?.id === payout?.id ? { ...p, routing_status: 'on_hold', status: 'on_hold' } : p
      ));
      showToast(`Paiement mis en suspens — ${payout?.recipient_name || 'Agent'}`);
    } catch (err) {
      showToast('Erreur lors du rejet', 'error');
    }
  }, [user?.id, showToast]);

  // ─── Fallback channel ─────────────────────────────────────────────────────

  const handleFallback = useCallback(async (payout, newChannel) => {
    try {
      const { error } = await supabase
        ?.from('commission_payouts')
        ?.update({
          payout_channel: newChannel,
          routing_status: 'pending',
          status: 'pending',
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', payout?.id);

      if (error) throw error;

      setPayouts(prev => prev?.map(p =>
        p?.id === payout?.id ? { ...p, payout_channel: newChannel, routing_status: 'pending', status: 'pending' } : p
      ));
      showToast(`Canal changé vers ${CHANNEL_CONFIG?.[newChannel]?.label || newChannel}`);
    } catch (err) {
      showToast('Erreur lors du changement de canal', 'error');
    }
  }, [showToast]);

  // ─── Batch processing ─────────────────────────────────────────────────────

  const approvedPayouts = payouts?.filter(p => (p?.routing_status || p?.status) === 'approved');

  const handleBatchProcess = useCallback(async () => {
    setBatchLoading(true);
    abortRef.current = new AbortController();

    try {
      const result = await withPaystackRetry(
        async ({ idempotencyKey }) => {
          // Mark all approved payouts as processing
          const ids = approvedPayouts?.map(p => p?.id);
          const { error } = await supabase
            ?.from('commission_payouts')
            ?.update({
              routing_status: 'processing',
              status: 'processing',
              batch_idempotency_key: idempotencyKey,
              processing_started_at: new Date()?.toISOString(),
              updated_at: new Date()?.toISOString(),
            })
            ?.in('id', ids);

          if (error) throw error;
          return { processed: ids?.length };
        },
        {
          payoutId: `batch-${Date.now()}`,
          signal: abortRef?.current?.signal,
          onRetry: (attempt, delayMs) => {
            setRetryCountdown(delayMs);
            const interval = setInterval(() => {
              setRetryCountdown(prev => {
                if (prev <= 100) { clearInterval(interval); return 0; }
                return prev - 100;
              });
            }, 100);
            countdownRef.current = interval;
          },
          onFinalFailure: ({ errorCode, errorInfo }) => {
            setErrorState({ errorCode });
          },
        }
      );

      if (result?.success) {
        setPayouts(prev => prev?.map(p =>
          approvedPayouts?.some(ap => ap?.id === p?.id)
            ? { ...p, routing_status: 'processing', status: 'processing' }
            : p
        ));
        showToast(`${approvedPayouts?.length} paiement(s) envoyés en traitement`);
        setShowBatchModal(false);
      } else {
        const errorCode = result?.errorCode || PAYSTACK_ERROR_CODES?.UNKNOWN;
        setErrorState({ errorCode });
        // Queue failed payouts for retry
        for (const p of approvedPayouts) {
          await queuePayoutForRetry({ payoutId: p?.id, errorCode });
          await logPaymentFailure({
            payoutId: p?.id,
            errorCode,
            errorMessage: result?.error?.message,
            attemptNumber: result?.attempts,
            channel: p?.payout_channel || 'paystack',
          });
        }
        showToast('Traitement en lot échoué — paiements mis en file de réessai', 'error');
      }
    } finally {
      setBatchLoading(false);
      setShowBatchModal(false);
    }
  }, [approvedPayouts, showToast]);

  // ─── Filter & search ──────────────────────────────────────────────────────

  const FILTERS = [
    { id: 'pending',  label: 'En attente', count: payouts?.filter(p => (p?.routing_status || p?.status) === 'pending')?.length },
    { id: 'approved', label: 'Approuvés',  count: payouts?.filter(p => (p?.routing_status || p?.status) === 'approved')?.length },
    { id: 'failed',   label: 'Échoués',    count: payouts?.filter(p => ['failed', 'retry_queued']?.includes(p?.routing_status || p?.status))?.length },
    { id: 'all',      label: 'Tous',       count: payouts?.length },
  ];

  const filteredPayouts = payouts?.filter(p => {
    const status = p?.routing_status || p?.status;
    const matchesFilter =
      activeFilter === 'all' ? true :
      activeFilter === 'failed' ? ['failed', 'retry_queued']?.includes(status) :
      status === activeFilter;

    const query = searchQuery?.toLowerCase();
    const matchesSearch = !query ||
      p?.recipient_name?.toLowerCase()?.includes(query) ||
      p?.transaction_ref?.toLowerCase()?.includes(query) ||
      p?.user_profiles?.full_name?.toLowerCase()?.includes(query);

    return matchesFilter && matchesSearch;
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>Vérification des Paiements — Agent | KapitalStates</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <Icon name="ArrowLeft" size={18} className="text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-gray-900 truncate">Vérification des Paiements</h1>
              <p className="text-xs text-gray-500">Approbation terrain · Lot de règlement</p>
            </div>
            <LiveBadge active={liveConnected} />
            <button
              onClick={() => { setRefreshing(true); fetchPayouts(); }}
              disabled={refreshing}
              className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <Icon name="RefreshCw" size={16} className={`text-gray-700 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4 pb-32">
          {/* Batch summary */}
          {!loading && <BatchSummaryCard payouts={payouts} />}

          {/* Error recovery banner */}
          {errorState && (
            <ErrorRecoveryBanner
              errorCode={errorState?.errorCode}
              retryCountdown={retryCountdown}
              onRetry={() => { setErrorState(null); handleBatchProcess(); }}
              onFallback={() => setErrorState(null)}
              onDismiss={() => setErrorState(null)}
            />
          )}

          {/* Search */}
          <div className="relative">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Rechercher un agent, une référence…"
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {FILTERS?.map((f) => (
              <button
                key={f?.id}
                onClick={() => setActiveFilter(f?.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-colors ${
                  activeFilter === f?.id
                    ? 'bg-gray-900 text-white' :'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {f?.label}
                {f?.count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    activeFilter === f?.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {f?.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Payout list */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 animate-pulse">
                  <div className="flex gap-3">
                    <div className="w-11 h-11 bg-gray-200 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPayouts?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="CheckCircle2" size={26} className="text-gray-400" />
              </div>
              <p className="text-sm font-bold text-gray-700">Aucun paiement</p>
              <p className="text-xs text-gray-400 mt-1">
                {activeFilter === 'pending' ? 'Tous les paiements ont été traités.' : 'Aucun résultat pour ce filtre.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPayouts?.map((payout) => (
                <RoutingVerificationCard
                  key={payout?.id}
                  payout={payout}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onFallback={handleFallback}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sticky batch action bar */}
        {approvedPayouts?.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 px-4 py-3 shadow-lg">
            <button
              onClick={() => setShowBatchModal(true)}
              className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-600 text-white rounded-2xl text-sm font-bold hover:bg-emerald-700 transition-colors shadow-lg"
            >
              <Icon name="Zap" size={16} />
              Lancer le traitement en lot · {approvedPayouts?.length} paiement{approvedPayouts?.length !== 1 ? 's' : ''}
              <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {formatAmount(approvedPayouts?.reduce((s, p) => s + (p?.net_payout_amount || p?.amount || 0), 0))}
              </span>
            </button>
          </div>
        )}

        {/* Batch confirmation modal */}
        {showBatchModal && (
          <BatchApprovalModal
            approvedPayouts={approvedPayouts}
            onConfirm={handleBatchProcess}
            onCancel={() => setShowBatchModal(false)}
            loading={batchLoading}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed top-16 left-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl shadow-lg text-sm font-semibold transition-all ${
            toast?.type === 'error' ?'bg-red-600 text-white' :'bg-gray-900 text-white'
          }`}>
            <Icon name={toast?.type === 'error' ? 'XCircle' : 'CheckCircle'} size={16} />
            {toast?.message}
          </div>
        )}
      </div>
    </>
  );
};

export default AgentPayoutVerification;
